import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './SettingsMenu.css';

const SettingsMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'nl');
  const [feedback, setFeedback] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || navigator.language.split('-')[0] || 'nl';
    setLanguage(savedLanguage);
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setStatusMsg(null);
  };

  const handleLanguageSelect = (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
    i18n.changeLanguage(newLang);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    setIsSubmitting(true);
    setStatusMsg(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const currentPage = window.location.hash || window.location.pathname || 'home';
      const response = await fetch(`${apiUrl}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          feedback,
          page: currentPage
        }),
      });

      if (response.ok) {
        setFeedback('');
        setStatusMsg({ type: 'success', text: t('feedback_submitted_successfully') || 'Bedankt voor je feedback!' });
        setTimeout(() => {
          setStatusMsg(null);
        }, 4000);
      } else {
        setStatusMsg({ type: 'error', text: t('error_submitting_feedback') || 'Fout bij verzenden van feedback.' });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setStatusMsg({ type: 'error', text: t('error_submitting_feedback') || 'Fout bij verzenden van feedback.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="settings">
      <button 
        className="settings__button" 
        onClick={toggleMenu}
        aria-label={t('settings') || 'Instellingen'}
        title={t('settings') || 'Instellingen'}
      >
        <i className="icon-settings"></i>
      </button>

      {/* Backdrop overlay */}
      <div 
        className={`settings__backdrop ${isOpen ? 'show' : ''}`}
        onClick={() => setIsOpen(false)}
      ></div>

      {/* Modal dialog */}
      <div className={`settings__modal ${isOpen ? 'show' : ''}`} role="dialog" aria-modal="true">
        <div className="settings__header">
          <div className="settings__header-title">
            <i className="icon-settings settings__header-icon"></i>
            <span>{t('settings') || 'Instellingen'}</span>
          </div>
          <button 
            type="button" 
            className="settings__close-button" 
            onClick={toggleMenu}
            aria-label="Sluiten"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="settings__body">
          {/* Section 1: Language */}
          <div className="settings__group">
            <label className="settings__label">
              <i className="fas fa-globe"></i> {t('language') || 'Taal'}
            </label>
            <div className="settings__lang-toggle">
              <button
                type="button"
                className={`settings__lang-btn ${language === 'nl' ? 'active' : ''}`}
                onClick={() => handleLanguageSelect('nl')}
              >
                <span className="lang-flag">🇳🇱</span>
                <span className="lang-text">Nederlands</span>
              </button>
              <button
                type="button"
                className={`settings__lang-btn ${language === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageSelect('en')}
              >
                <span className="lang-flag">🇬🇧</span>
                <span className="lang-text">English</span>
              </button>
            </div>
          </div>

          {/* Section 2: Feedback */}
          <div className="settings__group">
            <label className="settings__label" htmlFor="settings-feedback">
              <i className="fas fa-comment-dots"></i> {t('Feedback') || 'Feedback'}
            </label>
            <form onSubmit={handleFeedbackSubmit} className="settings__feedback-form">
              <textarea
                id="settings-feedback"
                className="settings__textarea"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t('leave_feedback') || 'Laat een reactie of suggestie achter...'}
                rows="3"
                maxLength={500}
                required
              ></textarea>
              
              {statusMsg && (
                <div className={`settings__status settings__status--${statusMsg.type}`}>
                  <i className={statusMsg.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}></i>
                  <span>{statusMsg.text}</span>
                </div>
              )}

              <button 
                type="submit" 
                className="settings__submit-btn" 
                disabled={isSubmitting || !feedback.trim()}
              >
                {isSubmitting ? (
                  <span><i className="fas fa-spinner fa-spin"></i> Verzenden...</span>
                ) : (
                  <span><i className="fas fa-paper-plane"></i> {t('submit') || 'Verstuur'}</span>
                )}
              </button>
            </form>
          </div>

          {/* Section 3: Analytics */}
          <div className="settings__group">
            <Link
              to="/analytics"
              className="settings__analytics-link"
              onClick={toggleMenu}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                color: "var(--first-color, #ff4c60)",
                fontWeight: "600",
                fontSize: "0.9rem",
                textDecoration: "none",
                padding: "8px 12px",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 76, 96, 0.1)"
              }}
            >
              📊 Bekijk Analytics & Stats
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsMenu;
